/**
 * useAllVirtualServers - Hook for fetching virtual servers from ALL MCP services
 * 
 * Blue Steel Excellence: Unified virtual server bundles across ALL servers
 * - Fetches virtual servers from all parent services in parallel
 * - Provides combined list with service origin metadata
 * - Caches per-service results
 * 
 * @example
 * ```tsx
 * const { virtualServers, loading, error } = useAllVirtualServers();
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import type { VirtualServer } from '../types';
import { useAllMcpServices, MCPService } from './useAllMcpServices';

export interface VirtualServerWithOrigin extends VirtualServer {
  parent_service_name?: string;
  parent_service_display_name?: string;
}

interface UseAllVirtualServersResult {
  virtualServers: VirtualServerWithOrigin[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  serviceCount: number;
}

// In-memory cache per service with 5-minute TTL
const vsCache = new Map<string, { data: VirtualServer[]; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

async function fetchVirtualServersForService(
  serviceId: string
): Promise<VirtualServer[]> {
  // Check cache
  const cached = vsCache.get(serviceId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  const response = await fetch(
    `/api/v1/mcp/virtual-servers?parent_server_id=${encodeURIComponent(serviceId)}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    console.warn(`[useAllVirtualServers] Failed to fetch VS for ${serviceId}: ${response.statusText}`);
    return [];
  }

  const data = await response.json();
  // API returns array directly OR { virtual_servers: [...] }
  const servers: VirtualServer[] = Array.isArray(data) ? data : (data.virtual_servers || []);

  // Update cache
  vsCache.set(serviceId, { data: servers, timestamp: Date.now() });

  return servers;
}

export function useAllVirtualServers(): UseAllVirtualServersResult {
  const { services, loading: servicesLoading, refresh: refreshServices } = useAllMcpServices();
  const [virtualServers, setVirtualServers] = useState<VirtualServerWithOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    vsCache.clear();
    refreshServices();
    setRefreshKey(prev => prev + 1);
  }, [refreshServices]);

  useEffect(() => {
    if (servicesLoading) {
      setLoading(true);
      return;
    }

    if (services.length === 0) {
      setVirtualServers([]);
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchAllVirtualServers = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch virtual servers from ALL services in parallel
        const results = await Promise.all(
          services.map(async (service) => {
            const vs = await fetchVirtualServersForService(service.id);
            return vs.map(v => ({
              ...v,
              parent_service_name: service.name,
              parent_service_display_name: service.display_name,
            }));
          })
        );

        // Flatten and sort by order, then by name
        const allVirtualServers = results
          .flat()
          .sort((a, b) => {
            // Sort by order first, then by name
            const orderDiff = (a.order || 0) - (b.order || 0);
            if (orderDiff !== 0) return orderDiff;
            return (a.display_name || a.name || '').localeCompare(b.display_name || b.name || '');
          });

        if (mounted) {
          setVirtualServers(allVirtualServers);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useAllVirtualServers] Error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    fetchAllVirtualServers();

    return () => {
      mounted = false;
    };
  }, [services, servicesLoading, refreshKey]);

  return {
    virtualServers,
    loading,
    error,
    refresh,
    serviceCount: services.length,
  };
}

/**
 * Clear the virtual servers cache
 */
export function clearAllVirtualServersCache(): void {
  vsCache.clear();
}

